const OVERLAY = '[role="dialog"], [role="alertdialog"]'
const SCOPE = `${OVERLAY}, form, [data-form-autofocus]`
const FIELD = 'input, textarea, select, [contenteditable="true"]'
const NON_TEXT_TYPES = new Set([
  'hidden',
  'button',
  'submit',
  'reset',
  'checkbox',
  'radio',
  'file',
  'range',
  'color',
])

function visible(element: HTMLElement): boolean {
  if (!element.isConnected || element.closest('[hidden], [inert], [aria-hidden="true"]'))
    return false
  const style = getComputedStyle(element)
  return (
    style.visibility !== 'hidden' &&
    style.display !== 'none' &&
    element.getClientRects().length > 0
  )
}

function editable(element: HTMLElement): boolean {
  if (
    !visible(element) ||
    element.matches(':disabled') ||
    element.closest('[aria-disabled="true"], [aria-busy="true"], .ant-spin-blur')
  )
    return false
  if (element instanceof HTMLInputElement) {
    // Read-only search inputs inside a Select are keyboard-operable selectors.
    const selector =
      element.matches('[role="combobox"]') && !!element.closest('.ant-select')
    return !NON_TEXT_TYPES.has(element.type) && (!element.readOnly || selector)
  }
  if (element instanceof HTMLTextAreaElement) return !element.readOnly
  return element instanceof HTMLSelectElement || element.isContentEditable
}

function selectable(
  element: HTMLElement
): element is HTMLInputElement | HTMLTextAreaElement {
  return (
    editable(element) &&
    (element instanceof HTMLTextAreaElement ||
      (element instanceof HTMLInputElement &&
        !element.readOnly &&
        ['text', 'search', 'tel', 'url', 'email', 'password', 'number'].includes(
          element.type
        )))
  )
}

function overlayLevel(element: HTMLElement): number {
  let level = 0
  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    const zIndex = Number.parseInt(getComputedStyle(node).zIndex, 10)
    if (Number.isFinite(zIndex)) level = Math.max(level, zIndex)
  }
  return level
}

/** Standard keyboard entry for all PC forms, including portalled dialogs/drawers.
 * Focus once per opening, not on every render. Existing values are selected on
 * focus so a keyboard/scanner can replace them without appending to old text.
 */
export function installFormKeyboardFocus(doc: Document = document): () => void {
  const scopes = new Map<HTMLElement, { key: string | null; handled: boolean }>()
  let frame = 0
  let selection: { field: HTMLInputElement | HTMLTextAreaElement; value: string } | null =
    null
  let pointer: { target: EventTarget | null; x: number; y: number } | null = null

  function selectValue(field: HTMLInputElement | HTMLTextAreaElement) {
    if (field.value) field.select()
    selection = { field, value: field.value }
  }

  function markHandled(root: HTMLElement) {
    for (const [scope, state] of scopes) {
      if (scope === root || scope.contains(root) || root.contains(scope))
        state.handled = true
    }
  }

  function refresh() {
    frame = 0
    const roots = Array.from(doc.querySelectorAll<HTMLElement>(SCOPE)).filter(visible)
    const live = new Set(roots)
    for (const root of scopes.keys()) if (!live.has(root)) scopes.delete(root)
    for (const root of roots) {
      const key = root.getAttribute('data-form-autofocus')
      if (!scopes.has(root) || scopes.get(root)!.key !== key)
        scopes.set(root, { key, handled: false })
    }

    // An upper confirmation dialog also blocks autofocus in the forms below it.
    const overlays = roots.filter((root) => root.matches(OVERLAY))
    const top = overlays.sort((a, b) => overlayLevel(a) - overlayLevel(b)).at(-1)
    const candidates = roots.filter(
      (root) => !scopes.get(root)!.handled && (!top || root === top || top.contains(root))
    )
    for (const root of candidates) {
      if (scopes.get(root)!.handled) continue
      const fields = Array.from(root.querySelectorAll<HTMLElement>(FIELD))
      const first = fields.find(
        (field) =>
          !field.closest('[data-form-autofocus-ignore]') &&
          field.tabIndex >= 0 &&
          editable(field)
      )
      if (!first) continue // Wait for asynchronous form data/fields to arrive.
      first.focus({ preventScroll: true })
      if (doc.activeElement !== first) continue
      if (selectable(first)) selectValue(first)
      markHandled(root)
      // Multiple page forms mount together: focus the first, not the last one.
      for (const other of candidates) scopes.get(other)!.handled = true
      break
    }

    // Initial values may arrive after the first focus. Select them only until
    // the user starts typing, clicking or composing; background updates must
    // never select text again while the user is editing it.
    if (
      selection &&
      doc.activeElement === selection.field &&
      selection.value !== selection.field.value
    )
      selectValue(selection.field)
  }

  function schedule() {
    if (!frame) frame = requestAnimationFrame(refresh)
  }

  function onFocus(event: FocusEvent) {
    const field = event.target
    selection = null
    if (field instanceof HTMLElement && selectable(field)) selectValue(field)
  }

  function onInteraction(event: Event) {
    selection = null
    if (event.target instanceof HTMLElement) {
      for (const [root, state] of scopes) {
        if (root.contains(event.target)) state.handled = true
      }
    }
  }

  function onPointerDown(event: PointerEvent) {
    onInteraction(event)
    pointer = { target: event.target, x: event.clientX, y: event.clientY }
  }

  function onPointerUp(event: PointerEvent) {
    // The browser's mouseup can collapse a selection made during focusin.
    // Re-select only for the click that entered a field; preserve drag selection
    // and subsequent clicks used to position the caret for a manual correction.
    const pending = selection
    if (
      pointer &&
      pending &&
      pointer.target === pending.field &&
      Math.hypot(event.clientX - pointer.x, event.clientY - pointer.y) < 5
    ) {
      requestAnimationFrame(() => {
        if (
          selection === pending &&
          doc.activeElement === pending.field &&
          pending.field.value === pending.value
        )
          selectValue(pending.field)
      })
    }
    pointer = null
  }

  const observer = new MutationObserver(() => {
    // Record closing immediately, even if a retained dialog is reopened before
    // the next animation frame. Otherwise it would keep its old handled state.
    for (const root of scopes.keys()) if (!visible(root)) scopes.delete(root)
    schedule()
  })
  observer.observe(doc.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: [
      'class',
      'style',
      'hidden',
      'disabled',
      'readonly',
      'aria-hidden',
      'aria-disabled',
      'aria-busy',
      'data-form-autofocus',
      'value',
    ],
  })
  doc.addEventListener('focusin', onFocus)
  doc.addEventListener('pointerdown', onPointerDown, true)
  doc.addEventListener('pointerup', onPointerUp)
  for (const event of ['keydown', 'input', 'compositionstart'])
    doc.addEventListener(event, onInteraction, true)
  doc.addEventListener('transitionend', schedule, true)
  doc.addEventListener('animationend', schedule, true)
  schedule()

  return () => {
    observer.disconnect()
    cancelAnimationFrame(frame)
    selection = null
    doc.removeEventListener('focusin', onFocus)
    doc.removeEventListener('pointerdown', onPointerDown, true)
    doc.removeEventListener('pointerup', onPointerUp)
    for (const event of ['keydown', 'input', 'compositionstart'])
      doc.removeEventListener(event, onInteraction, true)
    doc.removeEventListener('transitionend', schedule, true)
    doc.removeEventListener('animationend', schedule, true)
  }
}
